import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArrowLeft, Plus, Trash2, Edit2, Copy, CheckCircle2, Save, BarChart3, ToggleLeft, ToggleRight, Trash, Loader2, Image as ImageIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import MediaPicker from "../components/MediaPicker";

const MAX_OPTIONS = 16;

function PollPanelAppearance() {
  const { theme, refresh } = useTheme();
  const [bgImage, setBgImage] = useState(theme.poll_panel_bg_image || "");
  const [opacity, setOpacity] = useState(Number(theme.poll_panel_opacity ?? 100));
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setBgImage(theme.poll_panel_bg_image || "");
    setOpacity(Number(theme.poll_panel_opacity ?? 100));
  }, [theme.poll_panel_bg_image, theme.poll_panel_opacity]);

  const dirty =
    (bgImage || "") !== (theme.poll_panel_bg_image || "") ||
    Number(opacity) !== Number(theme.poll_panel_opacity ?? 100);

  const uploadImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd);
      if (r.data?.url) setBgImage(r.data.url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      try { e.target.value = ""; } catch (_e) { /* ignore */ }
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/settings/theme", { poll_panel_bg_image: bgImage, poll_panel_opacity: Number(opacity) });
      await refresh();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-8 rounded-md border border-black/10 bg-white p-5 shadow-sm">
      <h2 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
        <ImageIcon size={16} className="text-[#6CABDD]" />
        Panel Appearance
      </h2>
      <p className="mb-4 text-sm text-[var(--brand-body)]">
        Add a background image behind the vote options on the Featured Poll widget, and control how visible it is.
      </p>

      {error ? (
        <p className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]" data-testid="poll-panel-error">
          {error}
        </p>
      ) : null}

      <div>
        <label className="block label-kicker text-[var(--brand-body)]">Background image (optional)</label>
        {bgImage ? (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <div
              className="h-16 w-28 flex-shrink-0 rounded-md border border-black/10 bg-cover bg-center"
              style={{ backgroundImage: `url("${bgImage}")` }}
              aria-label="Current poll panel background image"
            />
            <div className="min-w-0 flex-1 truncate text-xs text-[var(--brand-body)]">{bgImage}</div>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="rounded-md border border-black/15 px-3 py-2 label-kicker hover:border-[#0A0F17]"
              data-testid="poll-panel-image-change"
            >
              From library
            </button>
            <label className="cursor-pointer rounded-md border border-black/15 px-3 py-2 label-kicker hover:border-[#0A0F17]">
              {uploading ? "Uploading…" : "Upload new"}
              <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} data-testid="poll-panel-image-upload" />
            </label>
            <button
              type="button"
              onClick={() => setBgImage("")}
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 label-kicker text-red-700 hover:border-red-600"
              data-testid="poll-panel-image-remove"
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid="poll-panel-image-pick"
              className="inline-flex items-center gap-2 rounded-md border border-dashed border-black/20 px-4 py-3 label-kicker hover:border-[#0A0F17]"
            >
              <ImageIcon size={14} /> Pick from Media Library
            </button>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-black/20 px-4 py-3 label-kicker hover:border-[#0A0F17]">
              {uploading ? "Uploading…" : "Upload from device"}
              <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} data-testid="poll-panel-image-upload-empty" />
            </label>
          </div>
        )}
      </div>

      <div className="mt-4">
        <label className="block label-kicker text-[var(--brand-body)]">Background image opacity</label>
        <div className="mt-2 flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={opacity}
            onChange={(e) => setOpacity(Number(e.target.value))}
            data-testid="poll-panel-opacity"
            className="flex-1 accent-[#0A0F17]"
          />
          <span className="w-12 text-right font-display text-sm font-bold tabular-nums">{opacity}%</span>
        </div>
        <p className="mt-1 text-xs italic text-[var(--brand-body)]">
          100% = image fully visible. 0% = washed out to white (image hidden).
        </p>
      </div>

      <div className="mt-5 flex items-center gap-3 border-t border-black/5 pt-4">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-black disabled:opacity-40"
          data-testid="poll-panel-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save appearance
        </button>
        {savedFlash ? (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600">
            <CheckCircle2 size={14} /> Saved
          </span>
        ) : null}
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => {
          if (url) setBgImage(url);
          setPickerOpen(false);
        }}
        accept="image"
      />
    </div>
  );
}

export default function AdminPolls() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form State — editingId is null when creating, or a poll id when editing
  const [editingId, setEditingId] = useState(null);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState([{ text: "" }, { text: "" }]); // Start with 2 empty options
  const [closed, setClosed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/polls");
      setPolls(r.data || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleOptionChange = (idx, val) => {
    setOptions((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], text: val };
      return copy;
    });
  };

  const addOptionField = () => {
    if (options.length >= MAX_OPTIONS) return;
    setOptions((prev) => [...prev, { text: "" }]);
  };

  const removeOptionField = (idx) => {
    if (options.length <= 2) return; // Minimum 2 options required
    setOptions((prev) => prev.filter((_, i) => i !== idx));
  };

  const resetForm = () => {
    setEditingId(null);
    setQuestion("");
    setOptions([{ text: "" }, { text: "" }]);
    setClosed(false);
    setError("");
  };

  const startEdit = (poll) => {
    setEditingId(poll.id);
    setQuestion(poll.question);
    setOptions((poll.options || []).map((o) => ({ id: o.id, text: o.text, votes: o.votes || 0 })));
    setClosed(poll.closed);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanQuestion = question.trim();
    const cleanOpts = options
      .map((o) => ({ id: o.id, text: o.text.trim(), votes: o.votes || 0 }))
      .filter((o) => o.text);

    if (!cleanQuestion) {
      setError("Please enter a question.");
      return;
    }
    if (cleanOpts.length < 2) {
      setError("Please provide at least 2 non-empty options.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        question: cleanQuestion,
        options: cleanOpts,
        closed,
      };
      if (editingId) {
        await api.put(`/admin/polls/${editingId}`, payload);
        setSuccess("Poll updated successfully!");
      } else {
        await api.post("/admin/polls", payload);
        setSuccess("Poll created successfully!");
      }
      resetForm();
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const duplicatePoll = async (poll) => {
    setDuplicatingId(poll.id);
    setError("");
    setSuccess("");
    try {
      const payload = {
        question: `${poll.question} (Copy)`,
        options: (poll.options || []).map((o) => ({ text: o.text, votes: 0 })),
        closed: false,
      };
      await api.post("/admin/polls", payload);
      setSuccess("Poll duplicated — find the copy at the top of the list, ready to edit.");
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDuplicatingId(null);
    }
  };

  const toggleClosed = async (poll) => {
    setError("");
    setSuccess("");
    try {
      const payload = {
        question: poll.question,
        options: poll.options,
        closed: !poll.closed,
      };
      await api.put(`/admin/polls/${poll.id}`, payload);
      setSuccess(`Voting ${!poll.closed ? "stopped" : "reopened"} successfully!`);
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const removePoll = async (pollId) => {
    if (!window.confirm("Are you sure you want to delete this poll entirely? This cannot be undone and will break any embedded views of this poll.")) return;
    setError("");
    setSuccess("");
    try {
      await api.delete(`/admin/polls/${pollId}`);
      setSuccess("Poll deleted successfully!");
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]">
      <Header />
      
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand-body)] uppercase tracking-wider hover:text-[var(--brand-ink)] mb-1"
            >
              <ArrowLeft size={12} /> Back to dashboard
            </Link>
            <h1 className="font-display text-2xl font-bold text-[var(--brand-ink)]">
              Fan Polls Manager
            </h1>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Create voting polls, see real-time results, or stop voting on active polls.
            </p>
          </div>
        </div>

        {/* Panel appearance — background image + opacity for the Featured Poll widget */}
        <PollPanelAppearance />

        {/* Create form panel */}
        <form onSubmit={handleSubmit} className="mb-8 rounded-md border border-black/10 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            {editingId ? <Edit2 size={16} className="text-[#6CABDD]" /> : <Plus size={16} className="text-[#6CABDD]" />}
            {editingId ? "Edit Poll" : "Create a New Poll"}
          </h2>

          {error ? (
            <p className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]" data-testid="poll-error">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="mb-4 rounded-sm border-l-2 border-emerald-500 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 font-bold" data-testid="poll-success">
              {success}
            </p>
          ) : null}

          <div className="space-y-4">
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Poll Question / Topic</label>
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="e.g. Who is our Player of the Month?"
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                data-testid="poll-question-input"
              />
            </div>

            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Options (2–{MAX_OPTIONS} required)</label>
              <div className="mt-1 space-y-2">
                {options.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={opt.text}
                      onChange={(e) => handleOptionChange(idx, e.target.value)}
                      placeholder={`Option #${idx + 1}`}
                      className="block flex-1 rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                      data-testid={`poll-option-input-${idx}`}
                    />
                    {options.length > 2 ? (
                      <button
                        type="button"
                        onClick={() => removeOptionField(idx)}
                        className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50 shrink-0"
                        title="Delete option"
                      >
                        <Trash size={14} />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
              {options.length < MAX_OPTIONS ? (
                <button
                  type="button"
                  onClick={addOptionField}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#6CABDD] hover:underline"
                  data-testid="poll-add-option"
                >
                  <Plus size={12} /> Add another option
                </button>
              ) : null}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-black/5 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-black disabled:opacity-40"
              data-testid="poll-submit"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editingId ? "Save changes" : "Create Poll"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                className="label-kicker text-[var(--brand-body)] underline underline-offset-4 hover:text-[var(--brand-ink)]"
                data-testid="poll-cancel-edit"
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>

        {/* Active/Closed Polls Results */}
        <div className="rounded-md border border-black/10 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2 border-b border-black/5 pb-2">
            <BarChart3 size={16} className="text-[#6CABDD]" />
            Polls &amp; Results
          </h2>

          {loading ? (
            <div className="space-y-4">
              {[1, 2].map((id) => (
                <div key={id} className="h-32 animate-pulse rounded-md border border-black/10 bg-[#F8FAFC]" />
              ))}
            </div>
          ) : polls.length === 0 ? (
            <p className="p-8 text-center text-sm text-[var(--brand-body)] italic">No polls configured. Initial seeds will load on restart.</p>
          ) : (
            <div className="space-y-6">
              {polls.map((poll) => {
                const totalVotes = (poll.options || []).reduce((acc, curr) => acc + (curr.votes || 0), 0);
                return (
                  <div key={poll.id} className="rounded-md border border-black/5 bg-[#F8FAFC] p-5 shadow-xs" data-testid={`poll-result-${poll.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 border-b border-black/5 pb-3">
                      <div>
                        <h3 className="font-display font-bold text-sm sm:text-base text-[var(--brand-ink)]">
                          {poll.question}
                        </h3>
                        <p className="mt-1 text-[11px] text-[var(--brand-body)] font-bold uppercase tracking-wider">
                          ID: <span className="font-mono font-bold text-[var(--brand-ink)]">{poll.id}</span> · Total Votes: {totalVotes}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Edit Button */}
                        <button
                          type="button"
                          onClick={() => startEdit(poll)}
                          className="inline-flex h-8 items-center gap-1 rounded-md border border-black/10 px-3 text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17] hover:bg-black/5"
                          data-testid={`poll-edit-${poll.id}`}
                        >
                          <Edit2 size={12} /> Edit
                        </button>

                        {/* Duplicate Button */}
                        <button
                          type="button"
                          onClick={() => duplicatePoll(poll)}
                          disabled={duplicatingId === poll.id}
                          className="inline-flex h-8 items-center gap-1 rounded-md border border-black/10 px-3 text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17] hover:bg-black/5 disabled:opacity-40"
                          data-testid={`poll-duplicate-${poll.id}`}
                        >
                          {duplicatingId === poll.id ? <Loader2 size={12} className="animate-spin" /> : <Copy size={12} />} Duplicate
                        </button>

                        {/* Toggle Stop Voting Button */}
                        <button
                          type="button"
                          onClick={() => toggleClosed(poll)}
                          className={`inline-flex h-8 items-center gap-1 rounded-md border px-3 text-xs font-semibold ${
                            poll.closed
                              ? "border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                              : "border-red-500 bg-red-50 text-red-700 hover:bg-red-100"
                          }`}
                          data-testid={`poll-toggle-closed-${poll.id}`}
                        >
                          {poll.closed ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                          {poll.closed ? "Reopen" : "Stop Voting"}
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => removePoll(poll.id)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-red-600 hover:bg-red-50"
                          title="Delete poll"
                          data-testid={`poll-delete-${poll.id}`}
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      {poll.options.map((opt) => {
                        const votesCount = opt.votes || 0;
                        const percentage = totalVotes > 0 ? Math.round((votesCount / totalVotes) * 100) : 0;
                        return (
                          <div key={opt.id} className="relative overflow-hidden rounded bg-white border border-black/5 p-2 flex flex-col justify-center">
                            <div
                              className="absolute inset-y-0 left-0 bg-[#6CABDD]/10 transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                            <div className="relative z-10 flex items-center justify-between gap-4 text-xs font-bold text-[var(--brand-ink)]">
                              <span>{opt.text}</span>
                              <span className="text-[var(--brand-body)]">
                                {percentage}% ({votesCount})
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
